import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { catchError, combineLatest, map, merge, of, pairwise, startWith, Subscription, switchMap, tap } from 'rxjs';
import {
    defaultIncrementalTypeBatchSize,
    watermarkKeyDatatype,
    rawModes,
    scheduleTypes
} from '../../constants/constants';
import { TenantIngestionJob } from '../../models/tenant-ingestion-job';
import * as tooltips from '../../constants/tooltips';
import { TenantSchedule } from '../../models/tenant-schedule';
import { AccordionComponent } from '@healthcatalyst/cashmere';
import BaseComponent from '../base.component';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import { TenantIngestionJobService } from 'src/app/core/services/application/tenant-ingestion-job.service';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { IngestionJobsListService } from './ingestion-jobs-list.service';
import { batchSizeErrorMessage, smallBatchSizeCheck } from '../../utils/batch-size.utils';
import { IngestionDeleteConfirmationModalService } from 'src/app/modules/platform/sources/entities/modals/ingestion-delete-confirmation-modal/ingestion-delete-confirmation-modal.service';
import { TenantIngestionIdentifiers } from '../../models/tenant-ingestion-identifiers';
import { TenantIngestionPriorityType } from 'src/app/core/models/tenant-ingestion-priority-type';
import { isFileConnectionType } from '../../utils/connection-type.util';
import { FeatureFlag } from '../../constants/feature-flags';
import { ConnectionFamilyType } from '../../models/connection-type';
import { TenantScheduleSummary } from '../../models/tenant-schedule-summary';
import { CronService } from '../cron-editor/cron.service';
import {
    RawModeOptionWithDisabled,
    shouldShowEntityMigrationForJob,
    shouldShowCsaForJob,
    isCsaOrCdc,
    getRawModeOptions,
    getRawModeOptionsWithoutEntityMigration,
    getSelectableRawModeOptions,
    filterCsaAndCdcOptionsByFeatureFlag
} from '../../utils/raw-mode.utils';
import { FeatureToggleService } from 'src/app/core/services/application/feature-toggle.service';

/** Model for jobs that bundles together form group, ingestion job data, assigned schedule */
export interface JobModelVM {
    /** Editing the job in the UI with a reactive form */
    form: FormGroup;
    /** The original job data */
    data: TenantIngestionJob;
    /** A control for instantly toggling active state with one click */
    instantActiveFlag: { value: boolean; disabled: boolean };
    /** True if job is being saved */
    isSaving?: boolean;
    /** True if the job has unsaved changes */
    hasUnsavedChanges?: boolean;
    /** Assigned schedule */
    schedule?: TenantSchedule;
    /** Assigned schedule string description */
    scheduleDescription?: string;
}

export interface JobListInputData {
    schedules: TenantScheduleSummary[];
    tenantId: string;
    sourceId: number;
    sourceConnectionFamily: ConnectionFamilyType | null;
    entityId: number | undefined;
    entity: string;
    jobs: TenantIngestionJob[];
}

@Component({
    selector: 'cdp-ingestion-jobs-list',
    templateUrl: './ingestion-jobs-list.component.html',
    styleUrls: ['./ingestion-jobs-list.component.scss'],
    standalone: false
})
export class IngestionJobsListComponent extends BaseComponent implements OnChanges {
    jobModels: Array<JobModelVM> = [];
    hasUnsavedChangesSub: Subscription;
    allChangesSub: Subscription;
    activeOrModeChangesSub: Subscription;
    listHasUnsavedChanges = false;
    rawModeOptions: RawModeOptionWithDisabled[];
    rawModeOptionsWithoutEntityMigration: RawModeOptionWithDisabled[];
    selectableRawModeOptions: RawModeOptionWithDisabled[];
    isIncrementalCdcEnabled = false;
    tooltips = tooltips;
    scheduleTypes = scheduleTypes;
    rawModes = rawModes;
    readonly featureflags = FeatureFlag;

    @Output() jobsWithUnsavedChanges = new EventEmitter<FormGroup[]>();
    @Output() jobsChanged = new EventEmitter<FormGroup[]>();
    @Output() refreshOnUpdate = new EventEmitter();
    @Output() ingestionToDelete = new EventEmitter<number>();
    @Output() calculateRefKeyCheck = new EventEmitter<FormGroup[]>();
    schedules: TenantScheduleSummary[];
    tenantId!: string;
    sourceId!: number;
    sourceConnectionFamily!: ConnectionFamilyType | null;
    entityId: number | undefined;
    entity: string;
    jobs: TenantIngestionJob[] = [];
    public refKeysRequiredMessage = tooltips.refKeysRequiredMessage;
    /** True if this list of jobs should allow for saving jobs individually, or false if the parent component will handle job saves. */
    @Input() canSaveOwnJobs: boolean;
    @Input() hasSourcesEditPermission = false;
    @Input() validatesActiveJobModes = false;
    @Input() schema?: string;
    @Input() hasRefKeys: boolean;
    @ViewChild(AccordionComponent, { static: false }) accordion!: AccordionComponent;
    _data: JobListInputData;
    @Input() get data(): JobListInputData {
        return this._data;
    }
    set data(data: JobListInputData) {
        this.jobs = data.jobs;
        this.tenantId = data.tenantId;
        this.schedules = data.schedules;
        this.sourceId = data.sourceId;
        this.sourceConnectionFamily = data.sourceConnectionFamily;
        this.entityId = data.entityId;
        this.entity = data.entity;

        this.setRawModeOptions();
        this.jobModels = this.setupJobForms(this.jobs);
        this.watchAllJobs();
    }

    constructor(
        private ingestionJobService: TenantIngestionJobService,
        public listService: IngestionJobsListService,
        private toast: ToastNotificationService,
        private notificationService: ToastNotificationService,
        private ingestionDeleteConfirmationModalService: IngestionDeleteConfirmationModalService,
        private cronService: CronService,
        private featureToggleService: FeatureToggleService,
        oktaAuth?: OktaAuthService
    ) {
        super(oktaAuth);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if hasRefKeys input changed
        if (changes['hasRefKeys'] && !changes['hasRefKeys'].firstChange) {
            this.disableorEnableModeOptions();
        }
        if (changes['hasSourcesEditPermission']) {
            this.jobModels.forEach(job => {
                if (this.hasSourcesEditPermission) {
                    job.form.enable();
                } else {
                    job.form.disable();
                }
            });
        }
    }

    isFull(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Full.name;
    }

    isMigration(formGroup: FormGroup): boolean {
        return (
            formGroup.controls['rawMode'].value === rawModes.SchemaMigration.name ||
            formGroup.controls['rawMode'].value === rawModes.EntityMigration.name
        );
    }

    isBackFill(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Backfill.name;
    }

    isIncremental(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Incremental.name;
    }

    isSyncDeletes(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.SyncDeletes.name;
    }

    // Incremental_CDC reuses Incremental_CSA's watermark/batching behavior
    isCsaOrCdcMode(formGroup: FormGroup): boolean {
        return isCsaOrCdc(formGroup.controls['rawMode'].value);
    }

    isCDC(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Incremental_CDC.name;
    }

    isAppend(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Append.name;
    }

    isReplace(formGroup: FormGroup): boolean {
        return formGroup.controls['rawMode'].value === rawModes.Replace.name;
    }

    batchSizeErrorMessage(formGroup: FormGroup): string {
        const batchSize = formGroup.controls['rawBatchSize'].value;
        return batchSizeErrorMessage(batchSize);
    }

    smallBatchSizeWarning(formGroup: FormGroup): boolean {
        const rawMode = formGroup.controls['rawMode'].value;
        const batchSize = formGroup.controls['rawBatchSize'].value;
        return smallBatchSizeCheck(rawMode, batchSize);
    }

    isWatermarkChange(job: JobModelVM): void {
        this.listService.checkWatermarkValueUpdate(job);
    }

    isHighPriority(formGroup: FormGroup): boolean {
        return formGroup.controls['priority'].value === TenantIngestionPriorityType.High;
    }

    get isFileSourceType(): boolean {
        return isFileConnectionType(this.sourceConnectionFamily);
    }

    get isWatermarkValueChange(): boolean {
        return this.listService.isWatermarkValueChange;
    }

    get quotedEntityName(): string {
        return this.entity ? `'${this.entity}'` : '';
    }

    getScheduleDescription(job: JobModelVM): string {
        if (job.scheduleDescription && job.scheduleDescription !== 'No Schedule') {
            return job.scheduleDescription;
        }

        if (job.data.tenantScheduleDescription) {
            return job.data.tenantScheduleDescription;
        }

        return 'No Schedule';
    }

    toggleActiveAndInstantSave(job: JobModelVM, event: MouseEvent): void {
        if (!this.hasSourcesEditPermission || job.instantActiveFlag.disabled) {
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        event.stopPropagation();
        const isActive = !job.instantActiveFlag.value;
        const currentJobModel = job.data;

        // apply change directly to model
        if (this.canSaveOwnJobs) {
            currentJobModel.activeFlag = isActive;
        }

        // keep state of main form in sync
        const activeFlagFormControl = job.form.controls['activeFlag'];
        activeFlagFormControl.setValue(isActive, { emitEvent: false });

        // only continue on to instant save if this is an existing job
        // and component is configured to save its own jobs
        if (!currentJobModel.tenantIngestionId || !this.canSaveOwnJobs) {
            return;
        }

        // save the job (but only changing the active flag, nothing else)
        activeFlagFormControl.markAsPristine();
        job.instantActiveFlag.disabled = true;
        job.isSaving = true;

        this.ingestionJobService
            .updateTenantIngestionJobs(currentJobModel)
            .pipe(this.unsubscribeOnDestroy())
            .subscribe({
                next: () => {
                    this.toast.showSuccess(
                        `Successfully ${isActive ? 'activated' : 'deactivated'} ${job.data.rawMode?.toLocaleLowerCase()} job for entity ${this.quotedEntityName}.`,
                        `${isActive ? 'Activated' : 'Deactivated'} Job `,
                        { timeout: 1000 }
                    );
                    job.instantActiveFlag.disabled = false;
                    job.isSaving = false;
                    this.refreshOnUpdate.emit();
                },
                error: err => {
                    job.instantActiveFlag.value = !isActive;
                    job.data.activeFlag = !isActive;
                    activeFlagFormControl.setValue(!isActive);
                    let message = `Unable to ${isActive ? 'activate' : 'deactivate'} job for entity ${this.quotedEntityName}. `;
                    message +=
                        err.status != 500 && err.error?.message
                            ? err.error.message
                            : 'Contact an admin if the problem continues.';
                    this.toast.showError(message, 'Update Failed', {
                        timeout: 2000
                    });
                    job.instantActiveFlag.disabled = false;
                    job.isSaving = false;
                }
            });
    }

    createNewJob() {
        const jobForm = this.listService.createNewJob(
            this.tenantId,
            this.sourceId,
            this.entity,
            this.entityId,
            this.hasRefKeys,
            this.jobModels
        );

        this.jobModels.push(jobForm);
        this.watchAllJobs();
        this.calculateRefKeyCheck.emit(this.jobModels.map(jm => jm.form));
    }

    onCancel(jobVM: JobModelVM) {
        if (jobVM.data.tenantIngestionId) {
            this.listService.revertJobForm(jobVM);
            jobVM.form.markAsPristine();
        } else {
            this.jobModels.splice(this.jobModels.indexOf(jobVM), 1);
        }
    }

    onSaveJobClicked(job: JobModelVM) {
        const form = job.form;
        form.markAllAsTouched();
        if (!form.valid) {
            return;
        }

        job.isSaving = true;
        const ingestionJob = this.listService.mapToJobModel(
            [form],
            this.tenantId,
            this.sourceId,
            this.isFileSourceType
        )[0];

        this.listService
            .saveJob(ingestionJob)
            .pipe(this.unsubscribeOnDestroy())
            .subscribe({
                next: () => {
                    this.toast.showSuccess(
                        `Successfully saved ${ingestionJob.rawMode?.toLocaleLowerCase()} job for entity ${this.quotedEntityName}.`,
                        'Saved Job'
                    );
                    job.isSaving = false;
                    job.hasUnsavedChanges = false;
                    this.updateLocalJobData(ingestionJob);
                    this.refreshOnUpdate.emit();
                    this.init();
                },
                error: err => {
                    let message = `Unable to save job for entity ${this.quotedEntityName}. `;
                    message +=
                        err.status != 500 && err.error?.message
                            ? err.error.message
                            : 'Contact an admin if the problem continues.';
                    this.toast.showError(message, 'Save Failed');
                    job.isSaving = false;
                }
            });
    }

    deleteIngestion(job: JobModelVM) {
        const formControls = job.form.controls;
        const ingestionId: number = formControls['ingestionId'].value;
        this.ingestionDeleteConfirmationModalService
            .openConfirmDelete(formControls['rawMode'].value, formControls['entity'].value, this.schema)
            .pipe(
                switchMap((result: boolean) => {
                    if (!result) {
                        return of(false);
                    }

                    if (!ingestionId) {
                        const ingestionToRemove: JobModelVM | undefined = this.jobModels.find(
                            j => j.data.newIngestionTempId === job.data.newIngestionTempId
                        );
                        if (ingestionToRemove) {
                            this.jobModels.splice(this.jobModels.indexOf(ingestionToRemove), 1);
                            this.watchAllJobs();
                            this.notificationService.showSuccess(
                                `Successfully deleted ${job.data.rawMode?.toLocaleLowerCase()} job from entity ${this.quotedEntityName}.`,
                                'Deleted Job'
                            );
                        }
                        this.calculateRefKeyCheck.emit(this.jobModels.map(jm => jm.form));
                        return of(true);
                    }

                    const ingestionIdentifiers: TenantIngestionIdentifiers = {
                        tenantId: this.tenantId,
                        sourceId: job.data.tenantSourceId,
                        entityId: job.data.tenantSourceEntityId!,
                        ingestionId: ingestionId
                    };

                    if (this.canSaveOwnJobs) {
                        return this.ingestionJobService.deleteSingleTenantIngestionJob(ingestionIdentifiers).pipe(
                            tap(() => {
                                this.refreshOnUpdate.emit();
                                this.notificationService.showSuccess(
                                    `Successfully deleted ${job.data.rawMode?.toLocaleLowerCase()} job from entity ${this.quotedEntityName}.`,
                                    'Deleted Job'
                                );
                            }),
                            map(() => true),
                            catchError(() => {
                                this.notificationService.showError(
                                    `An unexpected error occurred while attempting to delete the selected job for entity ${this.quotedEntityName}. Contact an admin if the problem continues.`,
                                    'Delete Failed'
                                );
                                return of(false);
                            })
                        );
                    } else {
                        const ingestionToRemove: JobModelVM | undefined = this.jobModels.find(
                            j => j.data.tenantIngestionId === job.data.tenantIngestionId
                        );
                        if (ingestionToRemove) {
                            this.jobModels.splice(this.jobModels.indexOf(ingestionToRemove), 1);
                            this.ingestionToDelete.emit(ingestionId);
                            this.calculateRefKeyCheck.emit(this.jobModels.map(jm => jm.form));
                            this.watchAllJobs();
                        }
                        return of(true);
                    }
                })
            )
            .subscribe();
    }

    public queueJob(job: JobModelVM) {
        if (!this.tenantId || !job.data.tenantIngestionId) {
            this.handleQueueFail(job);
            return;
        }
        this.ingestionJobService.queueTenantJobs(this.tenantId, [job.data.tenantIngestionId]).subscribe({
            next: () => {
                this.refreshOnUpdate.emit();
                this.toast.showSuccess(
                    `The ${job.data.rawMode} job for ${job.data.entity} has been queued.`,
                    'Queued Job'
                );
            },
            error: () => {
                this.handleQueueFail(job);
            }
        });
    }

    private handleQueueFail(job: JobModelVM) {
        this.toast.showError(
            `The ${job.data.rawMode ? job.data.rawMode + ' ' : ''}job for ${job.data.entity} was not able to be queued.`,
            'Queue Failed'
        );
    }

    // private methods
    private init() {
        this.jobModels = this.setupJobForms(this.jobs);
        this.watchAllJobs();
    }

    // this.jobs is only refreshed from the server asynchronously (refreshOnUpdate); without
    // this, init() rebuilds the just-saved job's form from stale pre-save data.
    private updateLocalJobData(savedJob: TenantIngestionJob): void {
        if (!savedJob.tenantIngestionId) {
            return;
        }
        const index = this.jobs.findIndex(j => j.tenantIngestionId === savedJob.tenantIngestionId);
        if (index >= 0) {
            this.jobs[index] = savedJob;
        }
    }

    private setupJobForms(data: TenantIngestionJob[]): Array<JobModelVM> {
        const forms: Array<JobModelVM> = [];

        data?.forEach(job => {
            const existingjobVM = this.jobModels.find(j => j.data.tenantIngestionId === job.tenantIngestionId);
            job.rawWatermarkKeyDatatypeOptions = [...Object.values(watermarkKeyDatatype)];

            if (job.rawMode === rawModes.Backfill.name && job.rawWatermarkKeyDatatype) {
                const defaultDatatypesOptions = Object.values(watermarkKeyDatatype);
                const flattenedDatatypesNamesValues = defaultDatatypesOptions.flatMap(dataType => [
                    dataType.name,
                    dataType.value
                ]);
                if (
                    !!job.rawWatermarkKeyDatatype &&
                    !flattenedDatatypesNamesValues.find(dataTypeValue => dataTypeValue === job.rawWatermarkKeyDatatype)
                ) {
                    job.rawWatermarkKeyDatatypeOptions = [
                        ...Object.values(watermarkKeyDatatype),
                        { name: job.rawWatermarkKeyDatatype, value: job.rawWatermarkKeyDatatype }
                    ];
                } else {
                    job.rawWatermarkKeyDatatypeOptions = [...Object.values(watermarkKeyDatatype)];
                }
            }

            // Replace new or saved jobs and skip over unsaved jobs.
            const jobVM =
                !existingjobVM || !existingjobVM.hasUnsavedChanges
                    ? this.listService.createJobVM(job, this.tenantId, this.sourceId, this.entity, this.schedules)
                    : existingjobVM;

            forms.push(jobVM);
        });
        const unsavedNewJobs = this.jobModels.filter(c => c.hasUnsavedChanges && !c.data.tenantIngestionId);
        forms.push(...unsavedNewJobs);

        if (!this.hasSourcesEditPermission) {
            forms.forEach(job => job.form.disable());
        }

        return forms;
    }

    private watchAllJobs() {
        this.hasUnsavedChangesSub?.unsubscribe();
        const allChanges = this.jobModels.map(c => c.form.valueChanges.pipe(startWith(null)));

        this.allChangesSub?.unsubscribe();
        this.allChangesSub = combineLatest([...(allChanges || [])])
            .pipe(startWith(undefined), this.unsubscribeOnDestroy())
            .subscribe(() => this.jobsChanged.emit(this.jobModels.map(jm => jm.form)));

        this.hasUnsavedChangesSub = combineLatest([...(allChanges || [])])
            .pipe(startWith(undefined), pairwise(), this.unsubscribeOnDestroy())
            .subscribe(() => {
                const jobsThatNeedSave = this.recalculateUnsavedChanges();
                this.emitUnsavedChanges(jobsThatNeedSave);
            });

        // watches the active state toggle in the body, keeps the header toggle in sync. Does NOT save.
        this.jobModels.forEach(job => {
            job.form.controls['activeFlag'].valueChanges.pipe(this.unsubscribeOnDestroy()).subscribe({
                next: isActive => {
                    job.instantActiveFlag.value = isActive;
                }
            });
        });

        this.jobModels.forEach(job => {
            job.form.controls['rawMode'].valueChanges
                .pipe(startWith(undefined), pairwise(), this.unsubscribeOnDestroy())
                .subscribe(([previous, next]) => {
                    if (previous === undefined && job.data.tenantIngestionId) {
                        this.calculateRefKeyCheck.emit(this.jobModels.map(jm => jm.form));
                        return;
                    }

                    this.listService.setValidatorsForRawMode(next ?? '', job.form, job.data);
                    this.handleBatchSizeDefault(job, next);
                    this.calculateRefKeyCheck.emit(this.jobModels.map(jm => jm.form));
                });
        });

        this.activeOrModeChangesSub?.unsubscribe();
        if (this.validatesActiveJobModes) {
            const forms = this.jobModels.map(jm => jm.form);
            const activeFlagChanges = forms.map(f => f.controls['activeFlag'].valueChanges);
            const rawModeChanges = forms.map(f => f.controls['rawMode'].valueChanges);
            this.activeOrModeChangesSub = merge(...activeFlagChanges.concat(rawModeChanges))
                .pipe(startWith(undefined), this.unsubscribeOnDestroy())
                .subscribe(() =>
                    setTimeout(() => {
                        this.listService.validateUpToOneActiveJobPerMode(forms);
                        this.jobsChanged.emit(this.jobModels.map(jm => jm.form));
                    })
                );
        }

        this.emitUnsavedChanges(this.recalculateUnsavedChanges());
    }

    private handleBatchSizeDefault(job: JobModelVM, next: any): void {
        const jobForm = job.form;

        if (
            next?.includes(rawModes.Incremental.name) ||
            next === rawModes.Replace.name ||
            next === rawModes.Append.name
        ) {
            jobForm.controls['rawBatchSize'].setValue(defaultIncrementalTypeBatchSize, { emitEvent: false });
        } else {
            jobForm.controls['rawBatchSize'].setValue(null, { emitEvent: false });
        }
    }

    onPriorityToggle(isChecked: boolean, jobForm: FormGroup): void {
        if (!this.hasSourcesEditPermission || jobForm.disabled) {
            return;
        }

        jobForm.controls['priority']?.setValue(
            isChecked ? TenantIngestionPriorityType.High : TenantIngestionPriorityType.Normal
        );
    }

    setRawModeOptions() {
        this.isIncrementalCdcEnabled =
            this.featureToggleService.getFeatureFlagsByName(FeatureFlag.EnableIncrementalCdcRawMode)?.enabled ?? false;
        this.rawModeOptions = getRawModeOptions(this.isFileSourceType);
        this.rawModeOptionsWithoutEntityMigration = getRawModeOptionsWithoutEntityMigration(this.isFileSourceType);
        this.selectableRawModeOptions = getSelectableRawModeOptions(
            this.isFileSourceType,
            this.isIncrementalCdcEnabled
        );

        // Apply initial ref key state
        this.disableorEnableModeOptions();
    }

    getRawModeOptionsForJob(job: JobModelVM): RawModeOptionWithDisabled[] {
        const currentRawMode = job.form.controls['rawMode'].value;

        // Remove Entity Migration from available options unless this specific job already uses it
        if (shouldShowEntityMigrationForJob(currentRawMode)) {
            return filterCsaAndCdcOptionsByFeatureFlag(this.rawModeOptions, this.isIncrementalCdcEnabled);
        }

        // Existing CSA jobs still show CSA so users can inspect or change them while the CDC replacement is dark.
        if (shouldShowCsaForJob(currentRawMode)) {
            if (this.isIncrementalCdcEnabled) {
                return this.rawModeOptionsWithoutEntityMigration;
            }

            return this.selectableRawModeOptions;
        }

        return this.selectableRawModeOptions;
    }

    disableorEnableModeOptions(): void {
        // Create new arrays to trigger change detection for all existing jobs
        this.rawModeOptions = this.rawModeOptions.map(option => ({
            ...option,
            disabled: option.refKeyRequired === true ? !this.hasRefKeys : false
        }));

        this.rawModeOptionsWithoutEntityMigration = this.rawModeOptionsWithoutEntityMigration.map(option => ({
            ...option,
            disabled: option.refKeyRequired === true ? !this.hasRefKeys : false
        }));

        this.selectableRawModeOptions = this.selectableRawModeOptions.map(option => ({
            ...option,
            disabled: option.refKeyRequired === true ? !this.hasRefKeys : false
        }));
    }

    private emitUnsavedChanges(jobsThatNeedSave: JobModelVM[]): void {
        if (jobsThatNeedSave.length > 0) {
            this.listHasUnsavedChanges = true;
            this.jobsWithUnsavedChanges.emit(jobsThatNeedSave.map(m => m.form));
        } else {
            this.listHasUnsavedChanges = false;
            this.jobsWithUnsavedChanges.emit([]);
        }
    }

    private recalculateUnsavedChanges(): JobModelVM[] {
        this.jobModels?.forEach(m => {
            const formValue = m.form.getRawValue();
            const scheduleId = formValue.schedule?.scheduleId;

            if (m.schedule?.tenantScheduleId !== scheduleId) {
                m.schedule = this.schedules?.find(s => s.tenantScheduleId === scheduleId);
                m.scheduleDescription = m.schedule?.cron
                    ? this.cronService.translateCronWithUtc(m.schedule.cron)
                    : 'No Schedule';
            }

            m.hasUnsavedChanges =
                !formValue.ingestionId || !this.listService.isEquivalent(formValue, m.data, this.isFileSourceType);
        });

        return this.jobModels.filter(m => m.hasUnsavedChanges);
    }
}
